import React, { useLayoutEffect, useRef, useState } from "react";

// ===================================================================
// The Crowd v2 — Graphique en secteurs (camembert) avec legende
//
// Composant partage. Palette monochrome : une seule teinte (le texte du
// theme) a opacite decroissante, comme l'original.
//
// Les couleurs sont lues sur les jetons du theme puis posees en
// attributs explicites (fill, stroke) : le SVG reste ainsi fidele
// lorsqu'il est capture par html2canvas pour l'export PDF, qui ne
// resout ni les variables CSS ni currentColor. Relues a chaque rendu
// pour suivre un changement de theme.
//
// Props : data [{ label, value }], unite (ex. "h")
// ===================================================================

const OPACITES = [0.85, 0.65, 0.5, 0.38, 0.28, 0.2, 0.14, 0.09];
const TAILLE = 220;
const RAYON = 92;

function GraphiqueSecteurs({ data, unite = "" }) {
  const ref = useRef(null);
  const [teintes, setTeintes] = useState({ trait: "#232220", fond: "#2c2a27", texte: "#f1efec" });

  useLayoutEffect(() => {
    if (!ref.current) return;
    const style = getComputedStyle(ref.current);
    const lire = (nom, defaut) => style.getPropertyValue(nom).trim() || defaut;
    const suivantes = {
      texte: lire("--v2-text", teintes.texte),
      fond: lire("--v2-card", teintes.fond),
      trait: lire("--v2-card", teintes.trait),
    };
    if (suivantes.texte !== teintes.texte || suivantes.fond !== teintes.fond) setTeintes(suivantes);
  });

  if (!data || data.length === 0) return null;
  const total = data.reduce((sum, d) => sum + d.value, 0);
  if (total === 0) return null;

  const centre = TAILLE / 2;
  let angle = -Math.PI / 2;
  const secteurs = data.map((d, i) => {
    const ouverture = (d.value / total) * 2 * Math.PI;
    const debut = angle;
    const fin = angle + ouverture;
    angle = fin;
    const grandArc = ouverture > Math.PI ? 1 : 0;
    const x1 = centre + RAYON * Math.cos(debut);
    const y1 = centre + RAYON * Math.sin(debut);
    const x2 = centre + RAYON * Math.cos(fin);
    const y2 = centre + RAYON * Math.sin(fin);
    // Secteur unique a 100 % : un arc de 360° ne se trace pas, on dessine un cercle
    const chemin = data.length === 1
      ? null
      : `M ${centre} ${centre} L ${x1} ${y1} A ${RAYON} ${RAYON} 0 ${grandArc} 1 ${x2} ${y2} Z`;
    return {
      ...d,
      chemin,
      opacite: OPACITES[i % OPACITES.length],
      pourcent: ((d.value / total) * 100).toFixed(1),
    };
  });

  return (
    <div className="v2-secteurs" ref={ref}>
      <svg width={TAILLE} height={TAILLE} viewBox={`0 0 ${TAILLE} ${TAILLE}`} role="img" aria-label="Répartition">
        {secteurs.map((s, i) =>
          s.chemin ? (
            <path key={i} d={s.chemin} fill={teintes.texte} fillOpacity={s.opacite} stroke={teintes.trait} strokeWidth="2">
              <title>{`${s.label} : ${s.value}${unite} (${s.pourcent} %)`}</title>
            </path>
          ) : (
            <circle key={i} cx={centre} cy={centre} r={RAYON} fill={teintes.texte} fillOpacity={s.opacite}>
              <title>{`${s.label} : ${s.value}${unite} (100 %)`}</title>
            </circle>
          )
        )}
      </svg>

      <ul className="v2-secteurs-legende">
        {secteurs.map((s, i) => (
          <li key={i} className="v2-secteurs-item">
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
              <rect width="12" height="12" rx="3" fill={teintes.texte} fillOpacity={s.opacite} />
            </svg>
            <span className="v2-secteurs-libelle">{s.label}</span>
            <span className="v2-secteurs-valeur">{s.value}{unite} · {s.pourcent} %</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default GraphiqueSecteurs;
